import { Stack } from '@mantine/core';
import { trackEvent } from '@repo/common/analytics';
import type { EntityType } from '@repo/common/enums/entityType';
import { getEntityTypeDisplayInfo } from '@repo/common/utils/entityDisplayUtils';
import { lowerCase } from 'lodash-es';
import { useAuthUser } from '../../../api';
import type { PolicyIssueStore } from '../../../pages/PolicyPage/tabs/PolicyIssues/store';
import { useCheckEntityUpdatePermission } from '../../../utils/authorization/roles';
import EntityPageDescription from '../../EntityPageLayout/EntityPageDescription';
import EntityPageTitle from '../../EntityPageLayout/EntityPageTitle';
import { SecodaEntityIcon } from '../../SecodaEntity';
import type { EntityDrawerStore } from '../store';

function EntityDrawerHeader({
	entityType,
	store,
	padding = 'xs',
}: {
	entityType: EntityType;
	store: EntityDrawerStore | PolicyIssueStore;
	padding?: string;
}) {
	const { label } = getEntityTypeDisplayInfo(entityType);
	const { user, workspace } = useAuthUser();

	const canUpdateEntity = useCheckEntityUpdatePermission({
		entity: store.entity,
	});
	const readOnlyUser = !canUpdateEntity;

	const update = (key: string) => (value: string) => {
		trackEvent(
			`${store.entity.entity_type}/${key}/update`,
			{ id: store.entity.id },
			user!,
			workspace
		);
		store.updateByKey(key)(value);
	};

	return (
		<Stack p={padding ?? 'xs'}>
			<EntityPageTitle
				icon={<SecodaEntityIcon entity={store.entity} />}
				placeholder={`Untitled ${lowerCase(label)}`}
				entity={store.entity}
				isReadOnly
				onChange={update('title')}
				size="lg"
			/>
			<EntityPageDescription
				entityId={store.entity.id}
				description={store.entity?.description ?? ''}
				onChange={update('description')}
				readOnly={readOnlyUser}
			/>
		</Stack>
	);
}

export default EntityDrawerHeader;
